import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import environmentImage from '../../static/images/environmentMaps/map.hdr';
import lighterModel from '../../static/images/assets/lighter.glb';
import mundeModel from '../../static/images/assets/munde.glb';

// Camera frustum is only ~0.08 units tall (see orthographic `d` below), so
// non-lighter models get uniformly scaled to fit within that frame.
const TARGET_MODEL_SIZE = 0.06;
const SWAP_INTERVAL_MS = 10000;

const ThreeScene = () => {
    const mountRef = useRef(null);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        const mount = mountRef.current;
        let loadedCount = 0;

        const onAssetLoaded = () => {
            loadedCount += 1;
            if (loadedCount === 3) {
                setIsLoaded(true);
            }
        };
        
        // Set up the scene
        const scene = new THREE.Scene();
    
        // Set up the camera
        const aspect = mount.clientWidth / mount.clientHeight;
        const d = 0.04;
        const camera = new THREE.OrthographicCamera(-d * aspect, d * aspect, d, -d, 1, 100);
        camera.position.set(0.4, 0.3, 1); // Set camera position for isometric view
        camera.lookAt(new THREE.Vector3(0, 0, 0));
    
        // Set up the renderer
        const renderer = new THREE.WebGLRenderer();
        renderer.setSize(mount.clientWidth, mount.clientHeight);
        renderer.toneMapping = THREE.ACESFilmicToneMapping; // Enable tone mapping
        renderer.toneMappingExposure = 0.5;
        mount.appendChild(renderer.domElement);

        // Set up OrbitControls
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true; // Enable damping (inertia)
        controls.dampingFactor = 0.25; // Damping factor
        controls.screenSpacePanning = false; // Do not allow panning
        controls.enablePan = false;

        // Load environment file
        new RGBELoader().load(environmentImage, (environmentMap) => {
            environmentMap.mapping = THREE.EquirectangularReflectionMapping;
            scene.environment = environmentMap;
            renderer.render(scene, camera);
            onAssetLoaded();
        });

        // Light
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.2); // Increased intensity
        scene.add(ambientLight);

        const spotLight1 = new THREE.SpotLight(0xffffaa, 10, 0, Math.PI / 2.5);
        spotLight1.position.set(-.1, -.1, .2);
        scene.add(spotLight1);

        const spotLight2 = new THREE.SpotLight(0xffffaa, 10, 0, Math.PI / 2.5);
        spotLight2.position.set(.1, -.1, -.1);
        scene.add(spotLight2);

        const spotLight3 = new THREE.SpotLight(0xffffaa, 10, 0, Math.PI / 2.5);
        spotLight3.position.set(-.1, .2, -.1);
        scene.add(spotLight3);

        // Set up materials
        const goldMaterial = new THREE.MeshStandardMaterial({
            color: 0xffb700,
            roughness: 0,
            metalness: 1
        });

        const bronzeMaterial = new THREE.MeshStandardMaterial({
            color: 0xcc7722,
            roughness: 0,
            metalness: 1
        });

        // Groups so the two models can be swapped in and out as a whole
        const lighterGroup = new THREE.Group();
        scene.add(lighterGroup);

        const mundeGroup = new THREE.Group();
        mundeGroup.visible = false;
        scene.add(mundeGroup);

        // Load the models
        const loader = new GLTFLoader();
        loader.load(lighterModel, (gltf) => {
            const model = gltf.scene;
            // Traverse the model and apply the gold material to all meshes
            model.traverse((node) => {
                if (node.isMesh) {
                    node.material = goldMaterial;
                }
            });
            lighterGroup.add(model);
            renderer.render(scene, camera);
            onAssetLoaded();
        }, undefined, (error) => {
            console.error('An error happened', error);
        });

        loader.load(mundeModel, (gltf) => {
            const model = gltf.scene;
            model.traverse((node) => {
                if (node.isMesh) {
                    node.material = bronzeMaterial;
                }
            });

            // Normalize the model's raw Blender scale (largest dimension was
            // ~6m) down to the same visual footprint as the lighter, and
            // center it so it rotates in place around the scene origin.
            const box = new THREE.Box3().setFromObject(model);
            const size = box.getSize(new THREE.Vector3());
            const center = box.getCenter(new THREE.Vector3());
            const maxDimension = Math.max(size.x, size.y, size.z);

            // Position is applied in unscaled units (world = position + scale
            // * localPoint), so the centering offset must be scaled too -
            // otherwise it dwarfs the now-tiny model and pushes it off-frame.
            const scale = TARGET_MODEL_SIZE / maxDimension;
            model.scale.setScalar(scale);
            model.position.copy(center).multiplyScalar(-scale);

            mundeGroup.add(model);
            renderer.render(scene, camera);
            onAssetLoaded();
        }, undefined, (error) => {
            console.error('An error happened', error);
        });

        // Add the striker
        const strikerGeometry = new THREE.CylinderGeometry(0.0035, 0.0035, .025, 32);
        const striker = new THREE.Mesh(strikerGeometry, goldMaterial);
        striker.position.x = 0.0076;
        striker.position.y = 0.0075;
        striker.position.z = 0.0010;

        // Add ridges to the striker
        const ridgeCount = 24;
        const ridgeHeight = .025;
        const ridgeWidth = .0003;
        const ridgeDepth = .0003;

        for (let i = 0; i < ridgeCount; i++) {
            const angle = (i / ridgeCount) * Math.PI * 2;
            const x = 0.0035 * Math.cos(angle);
            const z = 0.0035 * Math.sin(angle);
            const ridgeGeometry = new THREE.BoxGeometry(ridgeWidth, ridgeHeight, ridgeDepth);
            const ridge = new THREE.Mesh(ridgeGeometry, goldMaterial);
            ridge.position.set(x, 0, z);
            ridge.rotation.y = -angle + Math.PI/4;
            striker.add(ridge);
        }

        lighterGroup.add(striker);

        // Alternate which model is shown every 10 seconds
        let showingLighter = true;
        const swapIntervalId = setInterval(() => {
            showingLighter = !showingLighter;
            lighterGroup.visible = showingLighter;
            mundeGroup.visible = !showingLighter;
        }, SWAP_INTERVAL_MS);

        // Animation loop
        const animate = () => {
            requestAnimationFrame(animate);
            controls.update(); // Update controls
            renderer.render(scene, camera);
        };

        animate();

        // Clean up on component unmount
        return () => {
            setIsLoaded(false);
            clearInterval(swapIntervalId);
            mount.removeChild(renderer.domElement);
        };
    }, []);
  
    return (
        <div ref={mountRef} style={{
            width: '100%',
            height: '70vh',
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.2s ease-in',
        }} />
    );
};

export default ThreeScene;
